import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { Check, FitPointNote, SizePick, Result } from "../api/types";
import { Card, ConfMeter, TailorTag, toast, vibrate } from "../components/Bits";
import { SignInButtons } from "../components/SignInButtons";
import { Sketch } from "../components/Sketch";
import { spotClass } from "../lib/fit";
import { emptyFavDraft, useApp } from "../state";

export function isVneck(labels: string[]): boolean {
  return labels.some((l) => /v-?neck/i.test(l));
}

function ZoneInfo({ r, sel, zone }: { r: Result; sel: string; zone: string | null }) {
  const cur = r.by_size[sel];
  if (!cur) return null;
  if (!zone) {
    return (
      <div className="zinfo" id="zinfo">
        <div className="zhead">
          <h3>Size {sel}</h3>
          <span className={"v " + cur.overall.tone}>{cur.overall.label}</span>
        </div>
        <p>{cur.overall.why}</p>
        <p className="aside" style={{ fontSize: "1.02rem" }}>
          Hover or tap a glowing spot on the garment to read how it fits there.
        </p>
      </div>
    );
  }
  const z = cur.fit_points.find((x) => x.zone === zone);
  if (!z) return null;
  return (
    <div className="zinfo swap" id="zinfo" key={sel + zone}>
      <div className="zhead">
        <h3>{z.label}</h3>
        <span className={"v " + spotClass(z.step, z.tailor?.tone === "neutral")}>{z.verdict}</span>
        <ConfMeter c={z.confidence} />
      </div>
      <p>{z.text}</p>
      <TailorTag t={z.tailor} />
      <AfterNote z={z} />
      {z.personal_note && <p className="personal-note">{z.personal_note}</p>}
      <Why z={z} />
      {z.ask && <Ask text={z.ask} />}
    </div>
  );
}

/** A favorite in the same cut or rise is a better guide than one in another: ask for it. Starts a
 *  new favorite, so the one already saved stays. */
function Ask({ text }: { text: string }) {
  const nav = useNavigate();
  const { setFav } = useApp();
  return (
    <button
      type="button"
      className="ask"
      onClick={() => {
        setFav(() => emptyFavDraft());
        nav("/favorite");
      }}
    >
      {text}
    </button>
  );
}

/** Style insights: how the cut, rise or length is designed to fit, what we'd expect from that,
 *  and what this size measures. Set apart from the fit notes above it. Falls back to the short
 *  design note on older results. */
function Why({ z }: { z: FitPointNote }) {
  const text = z.reasoning?.text ?? z.design_note;
  if (!text) return null;
  return (
    <div className="style-insights">
      <span className="eyebrow">Style insights</span>
      <p>{text}</p>
    </div>
  );
}

/** The main note is the fit as it arrives; this is the one line on how washing or wear changes it. */
function AfterNote({ z }: { z: FitPointNote }) {
  if (!z.after) return null;
  const when = z.after.when.charAt(0).toUpperCase() + z.after.when.slice(1);
  return (
    <p className="after-note">
      <span className={"v " + spotClass(z.after.step, z.after.neutral)}>
        {when}: {z.after.verdict}
      </span>
    </p>
  );
}

const ORDINAL = ["1st", "2nd", "3rd", "4th", "5th"];

/** The sizes most likely to fit, side by side, most likely first. What each one trades shows below,
 *  on the sketch. */
function Picks({ picks, name, sel, onPick }: { picks: SizePick[]; name: (p: SizePick) => string; sel: string; onPick: (p: SizePick) => void }) {
  return (
    <>
      {picks.length > 1 && <p className="aside">Most likely to fit you well first.</p>}
      <div className="trade" role="group" aria-label="Sizes most likely to fit">
        {picks.map((p, i) => (
          <button key={p.range + p.size} className="trade-opt" type="button" aria-pressed={p.range + "|" + p.size === sel} onClick={() => onPick(p)}>
            {picks.length > 1 && <small className="pick-rank">{ORDINAL[i]}</small>}
            <b>{name(p)}</b>
          </button>
        ))}
      </div>
    </>
  );
}

function startOf(r: Result): string {
  return r.by_size[r.start_size] ? r.start_size : (r.focus_sizes[1] ?? r.sizes[0]);
}

/** The same size in another fit: "28" in Curvy is "28 Curvy". */
function sameSize(r: Result, size: string): string {
  const num = size.split(" ")[0];
  return r.sizes.find((s) => s.split(" ")[0] === num) ?? startOf(r);
}

export function Results({ check, animate }: { check: Check; animate: boolean }) {
  const { me } = useApp();
  const nav = useNavigate();
  const { newBoard } = useApp();
  const lead = check.result!;
  const ranges = lead.ranges;
  const notesOf = (id: string): Result | undefined => (ranges ? ranges.find((x) => x.id === id)?.result : lead);
  const picks = (lead.picks ?? []).filter((p) => notesOf(p.range)?.by_size[p.size]);
  const [rangeId, setRangeId] = useState(picks[0]?.range ?? ranges?.[0]?.id ?? "");
  const range = ranges?.find((x) => x.id === rangeId);
  const r = range?.result ?? lead;
  const [sel, setSel] = useState(picks[0]?.size ?? startOf(r));
  const picked = picks.some((p) => p.range === (range ? rangeId : p.range) && p.size === sel);
  const [open, setOpen] = useState(!picked);
  const [showAll, setShowAll] = useState(!r.focus_sizes.includes(sel));
  const [zone, setZone] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [pinsFx, setPinsFx] = useState(animate);
  const cur = r.by_size[sel];
  const vneck = isVneck(check.candidate.labels);
  const shown = useMemo(() => {
    const base = showAll || picks.length > 0 || !r.focus_sizes.length ? r.sizes : r.focus_sizes;
    return base.filter((s) => r.by_size[s]);
  }, [showAll, r, picks.length]);

  // Fire-and-forget "notes.viewed" when someone settles on a spot (debounced, once per size+spot).
  const sent = useRef(new Set<string>());
  useEffect(() => {
    if (!zone || !cur) return;
    const fpId = cur.fit_points.find((x) => x.zone === zone)?.fit_point;
    const key = sel + "|" + (fpId ?? zone);
    if (sent.current.has(key)) return;
    const id = window.setTimeout(() => {
      sent.current.add(key);
      api.event({ type: "notes.viewed", check_id: check.id, size_label: sel, fit_point: fpId }).catch(() => {});
    }, 700);
    return () => window.clearTimeout(id);
  }, [zone, sel, cur, check.id]);

  if (!cur) return null;
  const pickZone = (z: string) => {
    setZone(z);
    setTouched(true);
  };
  const choose = (id: string, size: string) => {
    if (ranges) setRangeId(id);
    setSel(size);
    setPinsFx(true);
    vibrate();
  };
  // The banner speaks for the sizes most likely to fit, wherever the shopper has tapped since.
  const best = picks.length ? picks.map((p) => notesOf(p.range)!.by_size[p.size].overall) : lead.focus_sizes.map((s) => lead.by_size[s]?.overall).filter(Boolean);
  const allBad = best.length > 0 && best.every((o) => o!.tone === "bad");
  const pins = cur.fit_points.map((z) => ({ zone: z.zone, cls: spotClass(z.step, z.tailor?.tone === "neutral") + (touched ? "" : " idle"), name: `${z.label}: ${z.verdict}` }));
  const refSize = check.reference.size_label;
  const isUsual = (s: string) => s === refSize || (!!range && s === `${refSize} ${range.label}`);
  const first = r.sizes[0];
  const last = r.sizes[r.sizes.length - 1];
  // A regular size is printed without its range ("28"); next to "26 Curvy" it needs one.
  const name = (p: SizePick) => (ranges && ranges.length > 1 && !p.size.includes(" ") ? `${p.size} ${ranges.find((x) => x.id === p.range)?.label ?? ""}`.trim() : p.size);
  const selKey = (range ? rangeId : (picks[0]?.range ?? "")) + "|" + sel;

  return (
    <div className={"results" + (animate ? " fx-results" : "")} id="results">
      {allBad ? (
        <div className="banner bad">
          <b>Dead straight: we don't think this fits you in any of these sizes.</b>
          <p className="small">{best[0]!.why} Your favorite is a better guide than the size label here.</p>
        </div>
      ) : (
        <div className="banner">
          <p className="aside">
            We won't pick a size for you. See what each one trades, then <mark>choose what matters to you.</mark>
          </p>
        </div>
      )}

      <Card tag="Try it on" mark="3">
        {picks.length > 0 && <Picks picks={picks} name={name} sel={selKey} onPick={(p) => choose(p.range, p.size)} />}
        {picks.length > 0 && (
          <button className="linkish" type="button" aria-expanded={open} onClick={() => setOpen((x) => !x)}>
            {open ? "Fewer sizes" : "More sizes"}
          </button>
        )}
        {(open || !picks.length) && ranges && ranges.length > 1 && (
          <div className={"tabs" + (ranges.length > 4 ? " all" : "")} role="tablist" aria-label="Fits">
            {ranges.map((x) => (
              <button
                key={x.id}
                className="tab"
                role="tab"
                type="button"
                aria-selected={x.id === rangeId}
                onClick={() => choose(x.id, x.id === rangeId ? sel : sameSize(x.result, sel))}
              >
                <b className="range-name">{x.label}</b>
              </button>
            ))}
          </div>
        )}
        {(open || !picks.length) && (
          <div className={"tabs" + (shown.length > 4 ? " all" : "")} role="tablist" aria-label="Sizes">
            {shown.map((s) => (
              <button key={s} className="tab" role="tab" type="button" aria-selected={s === sel} onClick={() => choose(rangeId, s)}>
                <b>{s}</b>
                <small>{isUsual(s) ? "Your usual" : r.by_size[s].overall.short}</small>
              </button>
            ))}
          </div>
        )}
        {!picks.length && r.sizes.length > r.focus_sizes.length && r.focus_sizes.length > 0 && (
          <button className="linkish" type="button" aria-expanded={showAll} onClick={() => setShowAll((x) => !x)}>
            {showAll ? "Fewer sizes" : `More sizes (${first} to ${last})`}
          </button>
        )}
        <div className="stage">
          <Sketch key={rangeId + sel} cat={check.category} pins={pins} vneck={vneck} active={zone} animate={pinsFx} onPick={pickZone} onHover={pickZone} />
          <ZoneInfo r={r} sel={sel} zone={zone} />
        </div>
        <div className="zdots">
          {cur.fit_points.map((z, i) => (
            <button
              key={z.fit_point}
              type="button"
              className={"zdot " + spotClass(z.step, z.tailor?.tone === "neutral")}
              aria-pressed={zone === z.zone}
              onClick={() => pickZone(z.zone)}
              onFocus={() => pickZone(z.zone)}
            >
              <i />
              <span>
                {i + 1} {z.label}
              </span>
            </button>
          ))}
        </div>
      </Card>

      {me.user.is_guest && (
        <Card tag="Keep these notes" mark="★" className="signin-card">
          <p className="aside">
            <b>Sign in so your next notes get sharper.</b> Your checks and fit reports come with you, on any device.
          </p>
          <SignInButtons next={`/check/${check.id}`} />
        </Card>
      )}

      {check.report ? (
        <div className="nudge">
          <p className="small">
            You told us how this one went. <span className="aside">Thank you.</span>
          </p>
          <button className="chip sm" type="button" onClick={() => nav(`/check/${check.id}/report`)}>
            See your answers
          </button>
        </div>
      ) : (
        <Card tag="What happens next" mark="✂">
          <p className="aside">
            Bought it, passed on it, or still deciding? Tap what happened, and <mark>your next notes get sharper.</mark>
          </p>
          <button className="btn primary" type="button" onClick={() => nav(`/check/${check.id}/report`)}>
            Tell us what happened
          </button>
          <button
            className="btn ghost"
            type="button"
            onClick={() => {
              toast("Saved. It's waiting in your history.");
              newBoard();
            }}
          >
            I'll come back later
          </button>
        </Card>
      )}
    </div>
  );
}
